import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MapModal, { LocationData } from '../MapModal';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface Route {
  id: number;
  name: string;
  start_location: string;
  end_location: string;
  distance_km: number;
  estimated_duration_minutes: number;
}

type Vehicle = {
  id: number;
  make: string;
  model: string;
  color: string;
  license_plate: string;
  picture_url?: string;
}

interface TripStopForm {
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  is_pickup_point: boolean;
  is_dropoff_point: boolean;
}

type MapTarget =
  | { type: 'start' }
  | { type: 'destination' }
  | { type: 'new-stop' }
  | { type: 'edit-stop'; index: number };

interface CreateTripProps {
  onTripCreated?: () => void;
  onCancel?: () => void;
}

const CreateTrip: React.FC<CreateTripProps> = ({ onTripCreated, onCancel }) => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<Route[]>([]);
  const [formData, setFormData] = useState({
    route_id: '',
    start_location: '',
    destination: '',
    departure_datetime: '',
    available_seats: 1,
    price_per_seat: '',
    notes: '',
    vehicle_id: ''
  });
  const [stopPoints, setStopPoints] = useState<TripStopForm[]>([]);
  const [mapTarget, setMapTarget] = useState<MapTarget | null>(null);
  const [loadingRouteStops, setLoadingRouteStops] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    fetchRoutes();
    fetchVehicles();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoadingRoutes(true);
      const response = await api.get('/routes');
      setRoutes(response.data.routes || []);
    } catch (err: any) {
      console.error('Failed to fetch routes:', err);
      setError('Failed to load routes');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/my-vehicles');
      setVehicles(response.data.vehicles || []);
    } catch (err: any) {
      console.error('Failed to fetch vehicles:', err);
      // Not critical, so we don't set an error state here
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const loadRouteStops = async (routeId: string) => {
    if (!routeId) return;
    try {
      setLoadingRouteStops(true);
      const response = await api.get(`/routes/${routeId}`, { params: { include_stops: true } });
      const route = response.data.route;
      const stops: TripStopForm[] = (route?.stop_points || []).map((sp: any) => ({
        name: sp.name,
        address: sp.address || undefined,
        latitude: parseFloat(sp.latitude),
        longitude: parseFloat(sp.longitude),
        is_pickup_point: sp.is_pickup_point !== false,
        is_dropoff_point: sp.is_dropoff_point !== false
      }));
      setStopPoints(stops);
      setFormData(prev => ({
        ...prev,
        start_location: prev.start_location || route?.start_location || '',
        destination: prev.destination || route?.end_location || ''
      }));
    } catch (err: any) {
      console.error('Failed to load route stop points:', err);
      setError('Failed to load stop points for the selected route');
    } finally {
      setLoadingRouteStops(false);
    }
  };

  const handleRouteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const routeId = e.target.value;
    setFormData(prev => ({ ...prev, route_id: routeId }));
    if (routeId) {
      loadRouteStops(routeId);
    }
  };

  const removeStopPoint = (index: number) => {
    setStopPoints(prev => prev.filter((_, i) => i !== index));
  };

  const updateStopPoint = (index: number, changes: Partial<TripStopForm>) => {
    setStopPoints(prev => prev.map((sp, i) => (i === index ? { ...sp, ...changes } : sp)));
  };

  const handleLocationSelected = (location: LocationData) => {
    const target = mapTarget;
    setMapTarget(null);
    if (!target) return;

    const label = location.address || location.name || `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;

    switch (target.type) {
      case 'start':
        setFormData(prev => ({ ...prev, start_location: label }));
        break;
      case 'destination':
        setFormData(prev => ({ ...prev, destination: label }));
        break;
      case 'new-stop':
        setStopPoints(prev => [...prev, {
          name: location.name || label.split(',')[0].trim(),
          address: location.address,
          latitude: location.lat,
          longitude: location.lng,
          is_pickup_point: true,
          is_dropoff_point: true
        }]);
        break;
      case 'edit-stop':
        updateStopPoint(target.index, {
          address: location.address,
          latitude: location.lat,
          longitude: location.lng
        });
        break;
    }
  };

  const handleVehicleChange = (vehicleId: string) => {
    setFormData(prev => ({
      ...prev,
      vehicle_id: vehicleId
    }));
  };

  const getVehiclePictureUrl = (vehicleId: number) => {
    return `${API_BASE_URL}/api/vehicles/${vehicleId}/picture`;
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // Minimum 30 minutes from now
    return now.toISOString().slice(0, 16);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.start_location || !formData.destination || !formData.departure_datetime || !formData.available_seats) {
        throw new Error('Start location, destination, departure time, and available seats are required');
      }

      if (stopPoints.some(sp => !sp.name.trim())) {
        throw new Error('Every stop point needs a name');
      }

      // Validate departure time is in the future
      const departureTime = new Date(formData.departure_datetime);
      const now = new Date();
      if (departureTime <= now) {
        throw new Error('Departure time must be in the future');
      }

      // Prepare trip data
      const tripData: any = {
        start_location: formData.start_location,
        destination: formData.destination,
        stop_points: stopPoints,
        departure_datetime: formData.departure_datetime,
        available_seats: formData.available_seats,
        notes: formData.notes || undefined
      };

      if (formData.route_id) {
        tripData.route_id = parseInt(formData.route_id);
      }

      // Add price if provided
      if (formData.price_per_seat) {
        tripData.price_per_seat = parseFloat(formData.price_per_seat);
      }

      // Add vehicle_id if selected
      if (formData.vehicle_id) {
        tripData.vehicle_id = parseInt(formData.vehicle_id);
      }

      const response = await api.post('/trips', tripData);
      if (response.status !== 201) {
        throw new Error('Failed to create trip');
      }

      setSuccess('Trip posted successfully!');

      // Reset form
      setFormData({
        route_id: '',
        start_location: '',
        destination: '',
        departure_datetime: '',
        available_seats: 1,
        price_per_seat: '',
        notes: '',
        vehicle_id: ''
      });
      setStopPoints([]);

      // Navigate back to trips after a short delay to show success message
      setTimeout(() => {
        navigate('/trips');
      }, 1500);

      if (onTripCreated) {
        onTripCreated();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  const selectedRoute = routes.find(route => route.id === parseInt(formData.route_id));

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Post a New Trip</h2>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Start & Destination */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {([
              { key: 'start_location', label: 'Start Location *', target: { type: 'start' } },
              { key: 'destination', label: 'Destination *', target: { type: 'destination' } }
            ] as const).map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name={field.key}
                    value={formData[field.key]}
                    onChange={handleInputChange}
                    required
                    placeholder="Type or pick on map"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setMapTarget(field.target)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                  >
                    Select on map
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Route Selection (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Route (Optional)
            </label>
            {loadingRoutes ? (
              <div className="animate-pulse">
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ) : (
              <select
                name="route_id"
                value={formData.route_id}
                onChange={handleRouteChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">No route</option>
                {routes.map(route => (
                  <option key={route.id} value={route.id}>
                    {route.name} ({route.start_location} → {route.end_location})
                  </option>
                ))}
              </select>
            )}
            {selectedRoute && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Distance:</span> {selectedRoute.distance_km} km |
                  <span className="font-medium ml-2">Estimated Duration:</span> {selectedRoute.estimated_duration_minutes} min
                </div>
                <button
                  type="button"
                  onClick={() => loadRouteStops(formData.route_id)}
                  disabled={loadingRouteStops}
                  className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                >
                  {loadingRouteStops ? 'Loading...' : 'Reload route stop points'}
                </button>
              </div>
            )}
          </div>

          {/* Stop Points */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Stop Points</h3>
              <button
                type="button"
                onClick={() => setMapTarget({ type: 'new-stop' })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
              >
                + Add stop point
              </button>
            </div>

            {stopPoints.length === 0 ? (
              <p className="text-sm text-gray-500">
                No stop points yet. Add stops by selecting them on the map{selectedRoute ? ' or reload them from the route' : ''}.
              </p>
            ) : (
              <div className="space-y-3">
                {stopPoints.map((stop, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 flex-shrink-0 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={stop.name}
                        onChange={(e) => updateStopPoint(index, { name: e.target.value })}
                        placeholder="Stop name"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="text-xs text-gray-500">
                        {stop.address ? `${stop.address} · ` : ''}{stop.latitude.toFixed(5)}, {stop.longitude.toFixed(5)}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={stop.is_pickup_point}
                            onChange={(e) => updateStopPoint(index, { is_pickup_point: e.target.checked })}
                          />
                          Pickup
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={stop.is_dropoff_point}
                            onChange={(e) => updateStopPoint(index, { is_dropoff_point: e.target.checked })}
                          />
                          Drop-off
                        </label>
                        <button
                          type="button"
                          onClick={() => setMapTarget({ type: 'edit-stop', index })}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          Change on map
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStopPoint(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                      aria-label={`Remove stop point ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Trip Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Departure Date & Time *
              </label>
              <input
                type="datetime-local"
                name="departure_datetime"
                value={formData.departure_datetime}
                onChange={handleInputChange}
                min={getMinDateTime()}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Seats *
              </label>
              <input
                type="number"
                name="available_seats"
                value={formData.available_seats}
                onChange={handleInputChange}
                min="1"
                max="8"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price per Seat (KSh)
            </label>
            <input
              type="number"
              name="price_per_seat"
              value={formData.price_per_seat}
              onChange={handleInputChange}
              min="0"
              step="50"
              placeholder="Leave empty for free ride"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Vehicle Information */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Vehicle (Optional)</h3>

            {vehicles.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="relative">
                      <label className="cursor-pointer">
                        <input
                          type="radio"
                          name="vehicle_id"
                          value={vehicle.id.toString()}
                          checked={formData.vehicle_id === vehicle.id.toString()}
                          onChange={(e) => handleVehicleChange(e.target.value)}
                          className="sr-only"
                        />
                        <div className={`border-2 rounded-lg p-4 transition-all ${formData.vehicle_id === vehicle.id.toString()
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}>
                          {/* Vehicle Image */}
                          <div className="aspect-w-16 aspect-h-9 mb-3">
                            {vehicle.picture_url ? (
                              <img
                                src={getVehiclePictureUrl(vehicle.id)}
                                alt={`${vehicle.make} ${vehicle.model}`}
                                className="w-full h-32 object-cover rounded-lg"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const fallback = target.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center ${vehicle.picture_url ? 'hidden' : 'flex'
                                }`}
                            >
                              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />
                              </svg>
                            </div>
                          </div>

                          {/* Vehicle Details */}
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900">
                              {vehicle.make} {vehicle.model}
                            </h4>
                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Color:</span> {vehicle.color}
                              </div>
                              <div>
                                <span className="font-medium">Plate:</span> {vehicle.license_plate}
                              </div>
                            </div>
                          </div>

                          {/* Selection Indicator */}
                          {formData.vehicle_id === vehicle.id.toString() && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Clear Selection */}
                {formData.vehicle_id && (
                  <button
                    type="button"
                    onClick={() => handleVehicleChange('')}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear vehicle selection
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />
                </svg>
                <p className="text-base">No vehicles available</p>
                <p className="text-sm mt-1">
                  <a href="/vehicles" className="text-blue-600 hover:text-blue-800 underline">
                    Add a vehicle
                  </a> to include it in your trips
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Any additional information for passengers (e.g., 'AC available', 'No smoking', etc.)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || loadingRoutes}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Posting...' : 'Post Trip'}
            </button>
          </div>
        </form>
      </div>

      <MapModal
        isOpen={mapTarget !== null}
        onClose={() => setMapTarget(null)}
        onLocationSelected={handleLocationSelected}
        title={
          mapTarget?.type === 'start' ? 'Select Start Location' :
          mapTarget?.type === 'destination' ? 'Select Destination' :
          'Select Stop Point'
        }
      />
    </div>
  );
};

export default CreateTrip;
